/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Capacity/CapacityTab" />
import { calculateTotalNetDaysOff } from "Agile/Common/Utils/CapacityDateUtils";
import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import { DateRange } from "Agile/Contracts/Date";
import * as React from "react";
import { IObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { IVssComponentContext, IVssLayoutManager, usePageContext, VssComponent } from "VSS/Platform/Layout";
import { Button, IButtonProps } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Dropdown, DropdownExpandableTextField } from "VSSUI/Dropdown";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { ColumnMore, ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { shiftToUTC } from "Wit/Common/DateUtils";
import { IBoardsMessagesService } from "Wit/Messages/BoardsMessagesService";
import { BoardsMessages } from "Wit/Messages/Components/BoardsMessages";
import { ISprintCapacityOptions } from "Wit/SprintsHub/Common/Contracts/Interfaces";
import { DaysOffDialog } from "Wit/SprintsHub/SprintsView/Components/Capacity/DaysOffDialog";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { ICapacityService, IUserActivity } from "Wit/SprintsHub/SprintsView/Services/ICapacityService";
import { MessageCardSeverity } from "VSSUI/MessageCard";

export interface ICapacityTabProps {
    selectedIteration?: IIterationData;
    teamSettings?: ITeamSettings;
}

export class CapacityTab extends VssComponent<ICapacityTabProps, {}> {
    private capacityOptions: ISprintCapacityOptions | undefined;
    private activityList: string[];
    private capacityService: ICapacityService;
    private personaDetailsProvider: PersonaDetailsProvider;
    private shortcutService: IVssKeyboardShortcutService;
    private messagesService: IBoardsMessagesService;

    constructor(props: ICapacityTabProps, context: IVssComponentContext) {
        super(props);

        this.capacityService = context.pageContext.getService<ICapacityService>("ICapacityService");
        
        this.messagesService = context.pageContext.getService<IBoardsMessagesService>("IBoardsMessagesService");

        this.capacityOptions = this.capacityService.getCapacityOptions();

        this.activityList = this.capacityOptions?.allowedActivities || [];

        if (!this.activityList.some(activity => equals(activity, Resources.Unassigned))) {
            this.activityList.unshift(Resources.Unassigned);
        }

        this.personaDetailsProvider = new PersonaDetailsProvider(context.pageContext);

        this.context = context;

        this.shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
    }

    public render() {
        return (
            <div className="page-content page-content-top capacity-content">
                <BoardsMessages className="margin-top-16 flex-noshrink min-icon-height" />

                <ConditionalChildren renderChildren={this.capacityService.getWaitingObservable()}>
                    <div className="absolute-fill capacity-async-work-layer" />
                </ConditionalChildren>
               
                <Card>
                    <Table columns={this.getColumns()} itemProvider={this.capacityService.getMemberCapacities()} />
                </Card>
            </div>
        );
    }

    public componentDidMount() {
        const { selectedIteration, teamSettings } = this.props;
        if (teamSettings && teamSettings.teamId && teamSettings.projectId && selectedIteration) {
            this.capacityService.loadCapacityData(teamSettings.teamId, teamSettings.projectId, selectedIteration.id);
        }
        this.registerShortcuts();

        this.messagesService.dismissMessage();

        this.renderCapacityWarning(this.capacityOptions?.isEditable);
    }

    private renderCapacityWarning = (isEditable: boolean | undefined): void => {
        if (isEditable === undefined || isEditable === true) {
            return;
        }

        const renderMessage = () => {
            return (
                <span>{Resources.UserHasNoEditPermissions}</span>
            );
        };

        this.messagesService.showMessage({
            renderMessage: renderMessage,
            severity: MessageCardSeverity.Info,
        });
    }

    private registerShortcuts = (): void => {
        this.shortcutService.registerShortcut(Resources.Capacity, {
            combos: ["z"],
            description: Resources.ToggleFullScreenMode,
            action: () => {
                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.toggleFullScreenMode();
            }
        });
    }

    public componentDidUpdate() {
        const { selectedIteration, teamSettings } = this.props;
        if (teamSettings && teamSettings.teamId && teamSettings.projectId && selectedIteration) {
            this.capacityService.loadCapacityData(teamSettings.teamId, teamSettings.projectId, selectedIteration.id);
        }
    }

    public componentWillUnmount() {
        this.unregisterShortcuts();
    }

    private unregisterShortcuts = () => {
        this.shortcutService.removeShortcutGroup(Resources.Capacity);
    };

    private getColumns(): ITableColumn<IUserActivity>[] {
        const tableColumns : ITableColumn<IUserActivity>[] = [
            {
                id: "user",
                name: Resources.User,
                renderCell: this.renderUserCell,
                width: 250
            },
            {
                id: "days-off",
                name: Resources.DaysOff,
                renderCell: this.renderDaysOffCell,
                width: 100
            },
            {
                id: "activity",
                name: Resources.Activity,
                renderCell: this.renderActivityCell,
                width: 175
            },
            {
                id: "capacity-per-day",
                name: Resources.CapacityPerDay,
                renderCell: this.renderCapacityCell,
                width: 200
            }];

        if (this.capacityOptions?.isEditable) {
            tableColumns.push(new ColumnMore(
                (userActivity, index) => {
                    return {
                        id: "capacity-actions",
                        items: [
                            {
                                iconProps: { iconName: "Add" },
                                id: "add-activity",
                                onActivate: () => this.capacityService.addActivity(userActivity, index),
                                text: Resources.AddActivity
                            },
                            {
                                iconProps: { iconName: "Cancel" },
                                id: "remove-activity",
                                onActivate: () => this.capacityService.removeActivity(index),
                                text: Resources.RemoveActivity
                            },
                            {
                                iconProps: { iconName: "Cancel" },
                                id: "remove-user",
                                onActivate: () => this.capacityService.removeUser(userActivity.teamMember!.id),
                                text: Resources.RemoveUser
                            }
                        ]
                    };
                },
                userActivity => {
                    return userActivity.teamMember !== undefined;
                }
            ))
        }
        return tableColumns;
    }

    private renderUserCell = (rowIndex: number, columnIndex: number, column: ITableColumn<IUserActivity>, tableItem: IUserActivity): JSX.Element => {
        if (!tableItem.daysOff) {
            return <td key={`empty-user-${rowIndex}`} />;
        }
        if (!tableItem.teamMember) {
            return (
                <SimpleTableCell columnIndex={columnIndex} key={`user-cell-${rowIndex}`}>
                    {Resources.TeamDaysOff}
                </SimpleTableCell>
            );
        }

        const imageUrl = getIdentityImageUrl(this.context.pageContext, tableItem.teamMember);

        return (
            <SimpleTableCell columnIndex={columnIndex} key={`user-cell-${rowIndex}`}>
                <Persona
                    className="margin-right-4"
                    identity={{ ...tableItem.teamMember, imageUrl: imageUrl }}
                    personaProvider={this.personaDetailsProvider}
                    size={PersonaSize.size24}
                />
                {tableItem.teamMember.displayName}
            </SimpleTableCell>
        );
    };

    private renderDaysOffCell = (
        rowIndex: number,
        columnIndex: number,
        column: ITableColumn<IUserActivity>,
        tableItem: IUserActivity
    ): JSX.Element => {
        if (!tableItem.daysOff) {
            return <td key={`empty-days-off-${rowIndex}`} />;
        }

        const { selectedIteration, teamSettings } = this.props;
        const teamDaysOffObservable = tableItem.teamMember ? this.capacityService.getTeamDaysOffObservable() : undefined;

        return (
            <SimpleTableCell columnIndex={columnIndex} key={`days-off-cell-${rowIndex}`}>
                <Observer daysOff={tableItem.daysOff} teamDaysOff={teamDaysOffObservable}>
                    {(obsProps: { daysOff: DateRange[]; teamDaysOff: DateRange[] | undefined }) => {
                        const totalDaysOff = calculateTotalNetDaysOff(
                            obsProps.daysOff,
                            teamSettings?.weekends.days ?? [],
                            obsProps.teamDaysOff,
                            selectedIteration?.startDate ? shiftToUTC(selectedIteration?.startDate!) : undefined,
                            selectedIteration?.finishDate ? shiftToUTC(selectedIteration?.finishDate!) : undefined
                        );

                        return (
                            <Link
                                disabled={!this.capacityOptions?.isEditable}
                                onClick={() => {
                                    const layoutManager = this.context.pageContext!.getService<IVssLayoutManager>("IVssLayoutManager");
                                    layoutManager.renderCallout(onDismiss => (
                                        <DaysOffDialog
                                            daysOff={obsProps.daysOff}
                                            onDismiss={daysOff => {
                                                this.capacityService.updateDaysOff(tableItem, daysOff);
                                                onDismiss();
                                            }}
                                            selectedIteration={selectedIteration}
                                            teamDaysOff={obsProps.teamDaysOff}
                                            teamSettings={teamSettings}
                                            user={tableItem.teamMember}
                                        />
                                    ));
                                }}
                            >
                                {totalDaysOff === 1 ? Resources.OneDayOff : format(Resources.XDaysOff, totalDaysOff)}
                            </Link>
                        );
                    }}
                </Observer>
            </SimpleTableCell>
        );
    };

    private renderActivityCell = (
        rowIndex: number,
        columnIndex: number,
        column: ITableColumn<IUserActivity>,
        tableItem: IUserActivity
    ): JSX.Element => {
        if (!tableItem.teamMember) {
            return (
                <SimpleTableCell className="team-activity-cell" columnIndex={columnIndex} key="team-activity-cell">
                    {Resources.TeamDaysOffMessage}
                </SimpleTableCell>
            );
        }
        const selection = new DropdownSelection();

        return (
            <SimpleTableCell columnIndex={columnIndex} key={`activity-cell-${rowIndex}`}>
                <Observer activityName={tableItem.activityName}>
                    {(obsProps: { activityName: string }) => {
                        return (
                            <ActivityCellDropdown
                                activityList={this.activityList}
                                activityName={obsProps.activityName}
                                rowIndex={rowIndex}
                                selection={selection}
                                userActivity={tableItem}
                                disabled={!this.capacityOptions?.isEditable}
                            />
                        );
                    }}
                </Observer>
            </SimpleTableCell>
        );
    };

    private renderCapacityCell = (
        rowIndex: number,
        columnIndex: number,
        column: ITableColumn<IUserActivity>,
        tableItem: IUserActivity
    ): JSX.Element => {
        if (!tableItem.teamMember) {
            return <SimpleTableCell columnIndex={columnIndex} key="team-capacity-row" />;
        }
        return (
            <SimpleTableCell columnIndex={columnIndex} key={`capacity-cell-${rowIndex}`}>
                <TextField
                    ariaLabel={Resources.CapacityPerDay}
                    inputType="number"
                    disabled={!this.capacityOptions?.isEditable}
                    onBlur={() => {
                        if (tableItem.capacityPerDay.value === undefined || tableItem.capacityPerDay.value === "") {
                            tableItem.capacityPerDay.value = "0";
                        }
                    }}
                    onChange={(ev, value) => {
                        if (parseFloat(value) >= 0) {
                            this.capacityService.updateCapacity(tableItem, value);
                        } else {
                            tableItem.capacityPerDay.value = ev.target.defaultValue;
                        }
                    }}                    
                    value={tableItem.capacityPerDay}
                />
            </SimpleTableCell>
        );
    };
}

interface ActivityCellDropdownProps {
    activityList: string[];
    activityName: string;
    rowIndex: number;
    selection: DropdownSelection;
    userActivity: IUserActivity;
    disabled?: boolean | undefined;
}

function ActivityCellDropdown({ activityList, activityName, rowIndex, selection, userActivity, disabled }: ActivityCellDropdownProps) {
    const pageContext = usePageContext();

    React.useEffect(() => {
        let selectionIndex = activityList.findIndex(value => equals(value, activityName));

        if (!selectionIndex || selectionIndex === -1) {
            selectionIndex = 0;
        }

        selection.value = [{ beginIndex: selectionIndex, endIndex: selectionIndex }];
    }, [activityName]);

    return (
        <Dropdown
            items={activityList}
            disabled={disabled}
            key={`activity-dropdown-${activityName}-${rowIndex}`}
            onSelect={(ev, activity) => pageContext.getService<ICapacityService>("ICapacityService").updateActivity(userActivity, activity.id)}
            renderExpandable={props => <DropdownExpandableTextField {...props} className="flex-row flex-grow" containerClassName="flex-grow" />}
            selection={selection}
        />
    );
}

export function renderDirtyButtonWrapper({ buttonProps, dirty, keyOverride }: { buttonProps: IButtonProps; dirty: IObservableValue<boolean>, keyOverride?: string }): JSX.Element {
    return (
        <Observer dirty={dirty} key={keyOverride ?? "revert-button-dirty-observer"}>
            {(obsProps: { dirty: boolean }) => <Button {...buttonProps} disabled={!obsProps.dirty} />}
        </Observer>
    );
}
